import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Notifications,
  Delete,
  CurrencyExchange,
  Payment,
  Add,
  Edit,
  CreditCard,
} from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { useUserPreferences } from '../hooks/useUserPreferences';
import { usePaymentMethods } from '../hooks/usePaymentMethods';
import { SUPPORTED_CURRENCY_CODES, getCurrencySymbol } from '../utils/currency';
import TestEmailReminder from '../components/TestEmailReminder';
import AutomatedReminderTest from '../components/AutomatedReminderTest';
import PaymentMethodModal from '../components/PaymentMethodModal';

const Settings: React.FC = () => {
  const { user } = useAuth();
  const { preferences, savePreferences } = useUserPreferences(user?.id);
  const { 
    paymentMethods, 
    addPaymentMethod, 
    updatePaymentMethod, 
    deletePaymentMethod, 
    isLoading: paymentMethodsLoading 
  } = usePaymentMethods(user?.id);
  
  const [settings, setSettings] = useState({
    email_notifications: true,
    whatsapp_notifications: false,
    reminder_days_before: 3,
    theme_mode: 'light',
    main_currency: 'USD',
  });

  // Payment methods modal state
  const [paymentMethodModalOpen, setPaymentMethodModalOpen] = useState(false);
  const [editingPaymentMethod, setEditingPaymentMethod] = useState<any>(null);



  // Update local state when preferences load
  React.useEffect(() => {
    if (preferences) {
      setSettings({
        email_notifications: preferences.email_notifications,
        whatsapp_notifications: preferences.whatsapp_notifications,
        reminder_days_before: preferences.reminder_days_before,
        theme_mode: preferences.theme_mode,
        main_currency: preferences.main_currency,
      });
    }
  }, [preferences]);

  const handleSettingChange = async (setting: string, value: any) => {
    const newSettings = { ...settings, [setting]: value };
    setSettings(newSettings);
    await savePreferences({ [setting]: value });
  };

  // Payment methods handlers
  const handleAddPaymentMethod = async (paymentMethodData: any) => {
    await addPaymentMethod(paymentMethodData);
  };

  const handleEditPaymentMethod = async (paymentMethodData: any) => {
    if (editingPaymentMethod) {
      await updatePaymentMethod({ id: editingPaymentMethod.id, ...paymentMethodData });
    }
  };

  const handleDeletePaymentMethod = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this payment method?')) {
      await deletePaymentMethod(id);
    }
  };

  const openEditModal = (paymentMethod: any) => {
    setEditingPaymentMethod(paymentMethod);
    setPaymentMethodModalOpen(true);
  };

  const openAddModal = () => {
    setEditingPaymentMethod(null);
    setPaymentMethodModalOpen(true);
  };

  const handleClosePaymentMethodModal = () => {
    setPaymentMethodModalOpen(false);
    setEditingPaymentMethod(null);
  };

  const deleteAccount = async () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      try {
        // Delete user data
        await supabase.from('subscriptions').delete().eq('user_id', user?.id);
        await supabase.from('users').delete().eq('id', user?.id);
        
        toast.success('Account deleted successfully');
        // Sign out user
        await supabase.auth.signOut();
      } catch (error) {
        toast.error('Failed to delete account');
        console.error('Error deleting account:', error);
      }
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Settings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your account preferences and notification settings.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Notification Settings */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Notifications />
              Notification Settings
            </Typography>
            
            <Box sx={{ mt: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.email_notifications}
                    onChange={(e) => handleSettingChange('email_notifications', e.target.checked)}
                  />
                }
                label="Email Notifications"
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                Receive email reminders before subscription renewals
              </Typography>
            </Box>

            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={false}
                    disabled
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography>WhatsApp Notifications</Typography>
                    <Box
                      sx={{
                        backgroundColor: 'warning.main',
                        color: 'warning.contrastText',
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                      }}
                    >
                      COMING SOON
                    </Box>
                  </Box>
                }
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                Receive WhatsApp reminders before subscription renewals
              </Typography>
              <Alert severity="info" sx={{ mt: 1, ml: 4 }}>
                WhatsApp integration is under development. Email notifications are currently available.
              </Alert>
            </Box>

            <Box sx={{ mt: 3 }}>
              <TextField
                fullWidth
                label="Reminder Days"
                type="number"
                value={settings.reminder_days_before}
                onChange={(e) => handleSettingChange('reminder_days_before', parseInt(e.target.value))}
                helperText="Number of days before renewal to send reminders (1-30 days)"
                inputProps={{ min: 1, max: 30 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                <strong>Example:</strong> If you set this to 3 days and have a subscription renewing on{' '}
                {new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toLocaleDateString()}, 
                you'll receive a reminder on {new Date().toLocaleDateString()}.
              </Typography>
            </Box>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>How it works:</strong> Reminders are automatically sent daily based on your settings. 
                You'll receive notifications for all subscriptions that are due for renewal within the specified number of days.
              </Typography>
            </Alert>
          </Paper>
        </Grid>

        {/* Currency Settings */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CurrencyExchange />
              Currency Settings
            </Typography>
            
            <Box sx={{ mt: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Main Currency</InputLabel>
                <Select
                  value={settings.main_currency}
                  onChange={(e) => handleSettingChange('main_currency', e.target.value)}
                  label="Main Currency"
                >
                  {SUPPORTED_CURRENCY_CODES.map((currencyCode) => (
                    <MenuItem key={currencyCode} value={currencyCode}>
                      {currencyCode} - {getCurrencySymbol(currencyCode)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This currency will be used as the default for displaying costs across the app
              </Typography>
              
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Real-time Conversion:</strong> Currency conversion rates are fetched from live APIs and update automatically every 30 minutes for accurate pricing.
                </Typography>
              </Alert>
            </Box>
          </Paper>
        </Grid>

        {/* Payment Methods */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Payment />
                Payment Methods
              </Typography>
              <Button
                variant="contained"
                onClick={openAddModal}
                startIcon={<Add />}
              >
                Add Payment Method
              </Button>
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Manage your payment methods for subscriptions. Add credit cards, PayPal accounts, and more.
            </Typography>

            {paymentMethodsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : paymentMethods && paymentMethods.length > 0 ? (
              <Grid container spacing={2}>
                {paymentMethods.map((paymentMethod) => (
                  <Grid item xs={12} sm={6} md={4} key={paymentMethod.id}>
                    <Card variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        {paymentMethod.type === 'credit_card' || paymentMethod.type === 'debit_card' ? (
                          <CreditCard color="primary" />
                        ) : (
                          <Payment color="primary" />
                        )}
                        <Typography variant="subtitle1" fontWeight="bold">
                          {paymentMethod.name}
                        </Typography>
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {paymentMethod.type.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => openEditModal(paymentMethod)}
                          startIcon={<Edit />}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => handleDeletePaymentMethod(paymentMethod.id)}
                          startIcon={<Delete />}
                        >
                          Delete
                        </Button>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Payment sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Payment Methods
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Add your first payment method to get started
                </Typography>
                <Button
                  variant="contained"
                  onClick={openAddModal}
                  startIcon={<Add />}
                >
                  Add Payment Method
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Test Email Reminders */}
        <Grid item xs={12}>
          <TestEmailReminder />
        </Grid>

        {/* Automated Reminder Test */}
        <Grid item xs={12}>
          <AutomatedReminderTest />
        </Grid>

        {/* Danger Zone */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 3, border: '2px solid', borderColor: 'error.main' }}>
            <Typography variant="h6" color="error" gutterBottom>
              Danger Zone
            </Typography>
            
            <Alert severity="warning" sx={{ mb: 2 }}>
              These actions are irreversible. Please proceed with caution.
            </Alert>

            <List>
              <ListItem>
                <ListItemText
                  primary="Delete Account"
                  secondary="Permanently delete your account and all associated data"
                />
                <ListItemSecondaryAction>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Delete />}
                    onClick={deleteAccount}
                  >
                    Delete Account
                  </Button>
                </ListItemSecondaryAction>
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>

      {/* Payment Method Modal */}
      <PaymentMethodModal
        open={paymentMethodModalOpen}
        onClose={handleClosePaymentMethodModal}
        paymentMethod={editingPaymentMethod}
        onSave={editingPaymentMethod ? handleEditPaymentMethod : handleAddPaymentMethod}
        isEditing={!!editingPaymentMethod}
      />
    </Container>
  );
};

export default Settings; 